import { type z } from 'zod';

import {
	AppFrameProfilingEventSchema,
	ClickedProfilingEventSchema,
	NavigationTimingProfilingEventSchema,
	type FrameProfilingEventType,
} from './frameEventValidations';
import { getPerformanceMark, type AppPerformanceProfilingEvent } from '.';

const ClickedProfilingCapturableEventTransform =
	ClickedProfilingEventSchema.transform<AppPerformanceProfilingEvent>((_) => {
		return {
			type: 'user-interacted',
			elapsed: getPerformanceMark(),
		};
	});

const NavigationTimingProfilingCapturableEventTransform =
	NavigationTimingProfilingEventSchema.transform<AppPerformanceProfilingEvent>((e) => {
		return {
			type: 'navigation-timing',
			elapsed: getPerformanceMark(),
			duration: Math.trunc(e.duration),
			transferSize: e.transferSize,
		};
	});

// Zod discriminated unions don't work with transforms on the narrowed types.
// Exhaustive-keyed map ensures future events added have transformers.
type ExhaustiveFrameEventTransformMap = {
	[K in FrameProfilingEventType]: z.ZodEffects<
		z.ZodTypeAny,
		AppPerformanceProfilingEvent,
		z.infer<typeof AppFrameProfilingEventSchema>
	>;
};

// For each in-frame event type, there should be a transformer to a safely ingestible event.
const transformers: ExhaustiveFrameEventTransformMap = {
	'navigation-timing': NavigationTimingProfilingCapturableEventTransform,
	click: ClickedProfilingCapturableEventTransform,
};

export const FrameProfilingEventCaptureTransformer =
	AppFrameProfilingEventSchema.transform<AppPerformanceProfilingEvent>((e) =>
		transformers[e.event].parse(e),
	);
